import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { findCategoryById, resolveCategoryId, formatPrice } from '@barter/shared';
import type { Ad } from '@barter/shared';
import { photoUrl } from '../../services/api-base';
import { Icon } from '../Icon/Icon';

function timeAgo(ts: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return t('timeAgo.justNow');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('timeAgo.minAgo', { count: min });
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return t('timeAgo.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 30) return t('timeAgo.daysAgo', { count: days });
  const lang = localStorage.getItem('barter-lang') || 'uk';
  const locale = lang === 'ro' ? 'ro-RO' : lang === 'ru' ? 'ru-RU' : 'uk-UA';
  return new Date(ts).toLocaleDateString(locale);
}

/** Бейдж «Нове» — объявлению меньше суток */
function isFresh(ad: Ad): boolean {
  return Date.now() - ad.createdAt < 24 * 60 * 60 * 1000;
}

export function AdCard({ ad }: { ad: Ad }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cat = findCategoryById(resolveCategoryId(ad.categoryId));

  return (
    <button className="offer-card" onClick={() => navigate(`/ad/${ad.id}`)} type="button">
      <div className="offer-card__photo">
        {ad.photos?.[0] ? (
          <img src={photoUrl(ad.photos[0])} alt={ad.title} loading="lazy" />
        ) : (
          <span className="offer-card__placeholder">
            <Icon name="camera" size={26} />
          </span>
        )}
        {isFresh(ad) && <span className="offer-card__fresh">{t('ad.fresh')}</span>}
        {ad.condition && (
          <span className={`offer-card__condition badge badge--${ad.condition}`}>
            {t(ad.condition === 'new' ? 'adDetails.conditionNew' : 'adDetails.conditionUsed')}
          </span>
        )}
      </div>
      <div className="offer-card__info">
        <div className="offer-card__price">
          {formatPrice(ad.price, ad.currency, t('createAd.negotiable'))}
        </div>
        <div className="offer-card__title">{ad.title}</div>
        {ad.wanted && (
          <div className="offer-card__wanted">
            <Icon name="repeat" size={13} /> {ad.wanted}
          </div>
        )}
        <div className="offer-card__meta">
          {cat && (
            <span>
              {cat.icon} {t(cat.nameKey ?? cat.id)}
            </span>
          )}
          {(ad.make || ad.model) && (
            <span className="offer-card__make">
              <Icon name="car" size={13} /> {[ad.make, ad.model].filter(Boolean).join(' ')}
            </span>
          )}
          {ad.location && (
            <span>
              <Icon name="location" size={13} /> {ad.location.city}
            </span>
          )}
          <span>{timeAgo(ad.createdAt, t)}</span>
        </div>
      </div>
    </button>
  );
}
